import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { MODULES, CONTAINER_SUB_KEYS, moduleIcon } from '../../modules';
import { mayOpen } from '../../services/homePage';
import { pageHref, OPEN_MENU_EVENT, OpenMenuAsk } from '../../services/newTab';
import { tabColor } from '../../tabPalette';
import { announcePage, openInSplit, samePagePath, splitHref, useOpenPages, SideMessage } from '../../services/splitView';

// ── Split view in the installed app (Calem, report 60878cab, 3rd opening) ────────────────────────────────────────
// "Build it the same way that Google does it. You right-click on a tab and bring it into split screen (starts at
// 50/50 but can be adjusted). Then, you simply select another tab to add to the dual/split view either two ways. By
// right-clicking another browser tab or by selecting the open tabs on the blank side of the split screen."
// "When the user downloads the system as an app on their desktop, allow the split screen to happen the same way."
// Three pieces, all mounted from App.tsx:
// · OpenMenuHost — the small menu a right-click on a tab or tile puts up in the installed app (services/newTab):
//   "Open in new window", which is what the right-click always did, or "Open in split view".
// · SplitView — the window's split view: the page it was on and the chosen page, side by side, each in a frame of
//   this same site, starting at half and half, with a divider to drag. An empty side lists the pages open in the
//   user's other windows as bubbles, one under another, and the user's own pages under those. Each side has an ×
//   that leaves the window on the other side's page alone.
// · PageAnnouncer — every page saying where it is, so those bubbles can be offered (services/splitView).

/** Every page's announcement of where it is and what it is called, on each move and each rename (the window title
 *  is set a moment after the address changes, by App's DocumentTitle and by some pages themselves). */
export function PageAnnouncer() {
  const loc = useLocation();
  useEffect(() => { announcePage(); }, [loc.pathname, loc.search, loc.hash]);
  useEffect(() => {
    const watch = new MutationObserver(() => announcePage());
    watch.observe(document.head, { childList: true, subtree: true, characterData: true });
    return () => watch.disconnect();
  }, []);
  return null;
}

const MENU_W = 236;
const MENU_H = 84;
const MENU_ITEM: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', border: 'none',
  borderRadius: 'var(--radius-md)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13,
  textAlign: 'left', cursor: 'pointer',
};
const hover = (on: boolean) => (e: React.MouseEvent<HTMLButtonElement>) => {
  e.currentTarget.style.background = on ? 'var(--c-gray-100)' : 'transparent';
};

/** The right-click's menu in the installed app, put up where the mouse is and kept inside the window. Esc, a click
 *  anywhere else, or the window losing focus puts it away again. */
export function OpenMenuHost() {
  const { t } = useTranslation();
  const [ask, setAsk] = useState<OpenMenuAsk | null>(null);
  useEffect(() => {
    const onAsk = (e: Event) => setAsk((e as CustomEvent<OpenMenuAsk>).detail);
    window.addEventListener(OPEN_MENU_EVENT, onAsk);
    return () => window.removeEventListener(OPEN_MENU_EVENT, onAsk);
  }, []);
  useEffect(() => {
    if (!ask) return;
    const close = () => setAsk(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('blur', close);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', close);
      window.removeEventListener('resize', close);
    };
  }, [ask]);
  if (!ask) return null;
  const left = Math.max(4, Math.min(ask.x, window.innerWidth - MENU_W - 4));
  const top = Math.max(4, Math.min(ask.y, window.innerHeight - MENU_H - 4));
  const pick = (split: boolean) => {
    const url = ask.url;
    setAsk(null);
    if (split) openInSplit(url);
    else window.open(url, '_blank');
  };
  return (
    <div onMouseDown={() => setAsk(null)} onContextMenu={e => { e.preventDefault(); setAsk(null); }}
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 100000 }}>
      <div role="menu" onMouseDown={e => e.stopPropagation()}
        style={{ position: 'absolute', left, top, width: MENU_W, padding: 4, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)' }}>
        <button type="button" role="menuitem" onClick={() => pick(false)} onMouseEnter={hover(true)} onMouseLeave={hover(false)} style={MENU_ITEM}>
          <i className="ti ti-external-link" style={{ fontSize: 15, color: 'var(--text-secondary)' }} aria-hidden />{t('splitv_new_window')}
        </button>
        <button type="button" role="menuitem" onClick={() => pick(true)} onMouseEnter={hover(true)} onMouseLeave={hover(false)} style={MENU_ITEM}>
          <i className="ti ti-arrows-horizontal" style={{ fontSize: 15, color: 'var(--text-secondary)' }} aria-hidden />{t('splitv_open')}
        </button>
      </div>
    </div>
  );
}

/** The page key an address belongs to — /m/<key>, or the Dashboard's /?tab=<key> — for its bubble's icon and colour. */
const keyOfRoute = (route: string): string => {
  try {
    const at = new URL(route, window.location.origin);
    if (at.pathname.startsWith('/m/')) return at.pathname.split('/')[2] || '';
    return at.pathname === '/' ? at.searchParams.get('tab') || '' : '';
  } catch { return ''; }
};

const PICK_HEAD: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4,
  margin: '16px 0 8px',
};
const bubble = (color?: string): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', maxWidth: 420, padding: '8px 14px', borderRadius: 999,
  border: `1px solid ${color || 'var(--border-strong)'}`, background: 'var(--bg-surface)', color: 'var(--text-primary)',
  fontSize: 13, textAlign: 'left', cursor: 'pointer',
});
const bubbleText: React.CSSProperties = { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

/** The empty side: the pages open in the user's other windows as bubbles, one under another, then the user's own
 *  pages — the ≡ menu's, by the same permission reading the Default page select takes (services/homePage: mayOpen). */
function Picker({ onPick }: { onPick: (path: string) => void }) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const open = useOpenPages();
  const mine = me ? MODULES.filter(m => !m.url && CONTAINER_SUB_KEYS.indexOf(m.key) < 0 && mayOpen(m.key, me)) : [];
  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 20px 20px', background: 'var(--bg-app)' }}>
      <div style={PICK_HEAD}>{t('splitv_open_now')}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {open.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('splitv_open_none')}</div>}
        {open.map(p => {
          const k = keyOfRoute(p.route);
          return (
            <button key={p.id} type="button" onClick={() => onPick(p.route)} title={p.route} style={bubble(tabColor(k))}>
              <i className={`ti ti-${moduleIcon(k)}`} style={{ fontSize: 15, color: tabColor(k) || 'var(--text-secondary)' }} aria-hidden />
              <span style={bubbleText}>{p.title || p.route}</span>
            </button>
          );
        })}
      </div>
      {mine.length > 0 && <div style={PICK_HEAD}>{t('splitv_your_pages')}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {mine.map(m => (
          <button key={m.key} type="button" onClick={() => onPick(pageHref(m.key))} style={bubble(tabColor(m.key))}>
            <i className={`ti ti-${m.icon}`} style={{ fontSize: 15, color: tabColor(m.key) || 'var(--text-secondary)' }} aria-hidden />
            <span style={bubbleText}>{t(`mod_${m.key}`)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

type Live = { route: string; title: string };
const pairFromAddress = (): string[] => {
  const q = new URLSearchParams(window.location.search);
  return [samePagePath(q.get('l')), samePagePath(q.get('r'))];
};

const SIDE_BAR: React.CSSProperties = {
  flex: 'none', height: 28, display: 'flex', alignItems: 'center', gap: 6, padding: '0 4px 0 12px',
  background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600,
  color: 'var(--text-secondary)',
};
const CLOSE_BTN: React.CSSProperties = {
  flex: 'none', width: 24, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none',
  borderRadius: 'var(--radius-md)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer',
};

/** The split view itself, at /split?l=<left page>&r=<right page> (App.tsx draws it in place of everything else). */
export default function SplitView() {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const loadMe = useStore(s => s.loadMe);
  const signedIn = !!localStorage.getItem('mf_token');
  const [pages, setPages] = useState<string[]>(pairFromAddress);              // what each side was given ('' = empty)
  const [live, setLive] = useState<Live[]>(() => pairFromAddress().map(route => ({ route, title: '' })));   // where it is now
  const [gen, setGen] = useState<number[]>([0, 0]);   // bumped at each placing, so even the same address loads afresh
  const [ratio, setRatio] = useState(0.5);
  const [dragging, setDragging] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const leftFrame = useRef<HTMLIFrameElement>(null);
  const rightFrame = useRef<HTMLIFrameElement>(null);
  const pagesNow = useRef(pages);
  pagesNow.current = pages;

  // Each side signs itself in with the token this window holds; with none, the window goes to the sign-in page whole.
  useEffect(() => {
    if (!signedIn) { window.location.replace('/login'); return; }
    if (!me) loadMe();
  }, []); // eslint-disable-line

  const put = useCallback((side: number, path: string) => {
    if (!path) return;
    setPages(p => p.map((x, i) => (i === side ? path : x)));
    setLive(l => l.map((x, i) => (i === side ? { route: path, title: '' } : x)));
    setGen(g => g.map((x, i) => (i === side ? x + 1 : x)));
  }, []);

  // What the two sides say: where each has got to, and a right-click's "Open in split view" — that page goes into the
  // empty side, or, with both sides full, into the side the right-click was NOT made in.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || !e.source) return;
      const from = e.source === leftFrame.current?.contentWindow ? 0 : e.source === rightFrame.current?.contentWindow ? 1 : -1;
      if (from < 0) return;
      const d = e.data as SideMessage;
      if (d?.slfSplit === 'here') {
        const route = samePagePath(d.route);
        if (route) setLive(l => l.map((x, i) => (i === from ? { route, title: String(d.title || '') } : x)));
      } else if (d?.slfSplit === 'open') {
        const p = pagesNow.current;
        put(!p[0] ? 0 : !p[1] ? 1 : from === 0 ? 1 : 0, samePagePath(d.url));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [put]);

  // The window's address keeps the pair as it is now, so a refresh comes back to it; its title names both pages.
  useEffect(() => {
    window.history.replaceState(window.history.state, '', splitHref(live[0].route, live[1].route));
    const names = live.map(x => x.title).filter(Boolean);
    document.title = names.length ? names.join(' | ') : t('splitv_title');
  }, [live, t]);

  // The divider. While it is held the two frames let the mouse through, or the first one it crossed would keep it.
  useEffect(() => {
    if (!dragging) return;
    const move = (e: PointerEvent) => {
      const r = box.current?.getBoundingClientRect();
      if (!r || r.width <= 0) return;
      setRatio(Math.min(0.85, Math.max(0.15, (e.clientX - r.left) / r.width)));
    };
    const stop = () => setDragging(false);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('blur', stop);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('blur', stop);
    };
  }, [dragging]);

  // × on a side: the window goes back to one page, the other side's, as far as it has got.
  const close = (side: number) => {
    const other = side === 0 ? 1 : 0;
    window.location.replace(live[other].route || pages[other] || '/');
  };

  if (!signedIn) return null;
  return (
    <div ref={box} style={{ height: '100vh', display: 'flex', background: 'var(--bg-app)', userSelect: dragging ? 'none' : undefined, cursor: dragging ? 'col-resize' : undefined }}>
      {[0, 1].map(i => (
        <React.Fragment key={i}>
          {i === 1 && (
            <div title={t('splitv_divider')}
              onPointerDown={e => { e.preventDefault(); setDragging(true); }} onDoubleClick={() => setRatio(0.5)}
              style={{ flex: 'none', width: 6, cursor: 'col-resize', background: dragging ? 'var(--accent)' : 'var(--border-strong)' }} />
          )}
          <div style={{ flex: `${i === 0 ? ratio : 1 - ratio} 1 0px`, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={SIDE_BAR}>
              <span style={bubbleText}>{pages[i] ? live[i].title || live[i].route : t('splitv_pick_title')}</span>
              <button type="button" onClick={() => close(i)} title={t('splitv_close')} aria-label={t('splitv_close')} style={CLOSE_BTN}>
                <i className="ti ti-x" aria-hidden />
              </button>
            </div>
            {pages[i]
              ? <iframe key={`${i}:${gen[i]}`} ref={i === 0 ? leftFrame : rightFrame} src={pages[i]} title={live[i].title || pages[i]}
                  style={{ flex: 1, minHeight: 0, width: '100%', border: 'none', display: 'block', pointerEvents: dragging ? 'none' : undefined }} />
              : <Picker onPick={path => put(i, path)} />}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}
